import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { formatCurrency } from "../utils/currency";
import { submitContractRequest } from "../api/contractRequestApi";

const StyledCard = styled(Card)(({ theme }) => ({
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    textAlign: "center",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    transition: "0.3s",
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[2],
    position: "relative",
    overflow: "hidden",
    "&:hover": {
        boxShadow: theme.shadows[5],
        transform: "translateY(-4px)",
    },
    "@media (hover: none)": {
        "&:hover": {
            boxShadow: theme.shadows[2],
            transform: "none",
        },
    },
}));

const PRIVACY_NOTICE =
    "The information entered in this request is not stored in the IronTrack database. It is sent only by email to the club, and a confirmation copy is emailed to you.";

const formatEuropeanDate = (isoDate) => {
    const [year, month, day] = String(isoDate || "").split("-");
    return year && month && day ? `${day}.${month}.${year}` : isoDate;
};

const ContractRequestPlans = ({ offerings = [], userData, paymentCurrency = "EUR" }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [selectedOffering, setSelectedOffering] = useState(null);
    const [startDate, setStartDate] = useState("");
    const [paymentDay, setPaymentDay] = useState("");
    const [answers, setAnswers] = useState({});
    const [errors, setErrors] = useState({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState({ open: false, severity: "success", message: "" });

    const orderedOfferings = [...offerings].sort((a, b) => (a.order || 0) - (b.order || 0));
    const orderedQuestions = selectedOffering
        ? [...(selectedOffering.questions || [])].sort((a, b) => (a.order || 0) - (b.order || 0))
        : [];

    const openRequest = (offering) => {
        setSelectedOffering(offering);
        setStartDate("");
        setPaymentDay("");
        setAnswers({});
        setErrors({});
    };

    const closeRequest = () => {
        if (submitting) return;
        setSelectedOffering(null);
        setConfirmOpen(false);
    };

    const validate = () => {
        const nextErrors = {};
        if (!startDate) {
            nextErrors.startDate = "Select a start date.";
        } else {
            const calendarDay = Number(startDate.split("-")[2]);
            if (calendarDay < 1 || calendarDay > 28) {
                nextErrors.startDate = "The start date must be between the 1st and 28th of a month.";
            }
        }
        if (!paymentDay) nextErrors.paymentDay = "Select a monthly payment due date.";
        orderedQuestions.forEach((question) => {
            if (!String(answers[question.id] ?? "").trim()) {
                nextErrors[question.id] = "This question is required.";
            }
        });
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const continueToConfirmation = () => {
        if (validate()) setConfirmOpen(true);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await submitContractRequest(selectedOffering.id, {
                startDate,
                paymentDay: Number(paymentDay),
                answers: orderedQuestions.map((question) => ({
                    questionId: question.id,
                    value: answers[question.id],
                })),
            });
            setConfirmOpen(false);
            setSelectedOffering(null);
            setNotification({
                open: true,
                severity: "success",
                message: "Your request was emailed to the club and to you. The request data was not stored in the database.",
            });
        } catch (error) {
            setConfirmOpen(false);
            setNotification({
                open: true,
                severity: "error",
                message: error.message || "Could not submit your contract request.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (offerings.length === 0) return null;

    return (
        <Box sx={{ width: "100%", mt: 6 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight="bold">Contracts</Typography>
                <Typography variant="body2" color="text.secondary">
                    Request a recurring monthly membership contract.
                </Typography>
            </Box>

            <Grid container spacing={3} justifyContent="center">
                {orderedOfferings.map((offering) => (
                    <Grid item xs={12} sm={6} md={4} key={offering.id}>
                        <StyledCard>
                            <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
                                <Box sx={{ mb: 1, display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Typography component="h3" variant="h4" fontWeight="bold" textAlign="center">
                                        {offering.name}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "center", mb: 2 }}>
                                    <Typography component="h3" variant="h5" fontWeight="bold">
                                        {formatCurrency(offering.monthlyPrice, paymentCurrency)}
                                    </Typography>
                                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                                        / month
                                    </Typography>
                                </Box>
                                <Divider sx={{ my: 2, opacity: 0.8, borderColor: "divider" }} />
                                <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center", justifyContent: "center" }}>
                                    <CheckCircleRoundedIcon color="primary" />
                                    <Typography variant="body1">Monthly contract</Typography>
                                </Box>
                                {offering.additionalInfo && (
                                    <Box sx={{ mt: "auto", mb: 2 }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                            {offering.additionalInfo}
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                            <CardActions>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    color="primary"
                                    onClick={() => openRequest(offering)}
                                    sx={{ borderRadius: "20px", py: 1 }}
                                >
                                    Request contract
                                </Button>
                            </CardActions>
                        </StyledCard>
                    </Grid>
                ))}
            </Grid>

            <Dialog
                open={Boolean(selectedOffering)}
                onClose={closeRequest}
                fullWidth
                maxWidth="sm"
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        m: isMobile ? 0 : 2,
                        maxHeight: isMobile ? "100%" : "calc(100% - 32px)",
                    },
                }}
            >
                <DialogTitle sx={{ pr: 1, pb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="span" fontWeight={700} display="block">
                                Request {selectedOffering?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {formatCurrency(selectedOffering?.monthlyPrice, paymentCurrency)} / month
                            </Typography>
                        </Box>
                        <IconButton onClick={closeRequest} edge="end" aria-label="Close" disabled={submitting}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
                    <Stack spacing={2.5}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: "action.hover",
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                                Customer
                            </Typography>
                            <Typography fontWeight={600}>{userData?.fullName || "Name unavailable"}</Typography>
                            <Typography variant="body2" color="text.secondary">{userData?.email || "Email unavailable"}</Typography>
                        </Box>

                        {selectedOffering?.additionalInfo && (
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-line" }}>
                                {selectedOffering.additionalInfo}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                                gap: 2,
                            }}
                        >
                            <TextField
                                label="Start date"
                                type="date"
                                value={startDate}
                                onChange={(event) => {
                                    const value = event.target.value;
                                    const calendarDay = Number(value.split("-")[2]);
                                    if (value && (calendarDay < 1 || calendarDay > 28)) {
                                        setStartDate("");
                                        setErrors((current) => ({
                                            ...current,
                                            startDate: "The start date must be between the 1st and 28th of a month.",
                                        }));
                                        return;
                                    }
                                    setStartDate(value);
                                    setErrors((current) => ({ ...current, startDate: "" }));
                                }}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ lang: "et-EE" }}
                                error={Boolean(errors.startDate)}
                                helperText={errors.startDate || "Day must be between 1 and 28."}
                                required
                                fullWidth
                            />

                            <FormControl fullWidth error={Boolean(errors.paymentDay)} required>
                                <InputLabel>Payment day</InputLabel>
                                <Select
                                    value={paymentDay}
                                    label="Payment day"
                                    onChange={(event) => {
                                        setPaymentDay(event.target.value);
                                        setErrors((current) => ({ ...current, paymentDay: "" }));
                                    }}
                                >
                                    {(selectedOffering?.paymentDayMode === "FIXED"
                                        ? selectedOffering.fixedPaymentDays || []
                                        : Array.from({ length: 28 }, (_, index) => index + 1)
                                    ).map((day) => (
                                        <MenuItem value={day} key={day}>Day {day}</MenuItem>
                                    ))}
                                </Select>
                                <Typography variant="caption" color={errors.paymentDay ? "error" : "text.secondary"} sx={{ mt: 0.5, ml: 1.75 }}>
                                    {errors.paymentDay || "Day of each month the fee is due."}
                                </Typography>
                            </FormControl>
                        </Box>

                        {orderedQuestions.map((question) => (
                            question.type === "radio" ? (
                                <FormControl
                                    key={question.id}
                                    error={Boolean(errors[question.id])}
                                    required
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: 1,
                                        borderColor: errors[question.id] ? "error.main" : "divider",
                                    }}
                                >
                                    <FormLabel sx={{ mb: 1, fontWeight: 600 }}>{question.question}</FormLabel>
                                    <RadioGroup
                                        value={answers[question.id] || ""}
                                        onChange={(event) => {
                                            setAnswers((current) => ({ ...current, [question.id]: event.target.value }));
                                            setErrors((current) => ({ ...current, [question.id]: "" }));
                                        }}
                                    >
                                        {(question.options || []).map((option) => (
                                            <FormControlLabel
                                                key={option.label}
                                                value={option.label}
                                                control={<Radio />}
                                                sx={{
                                                    alignItems: "flex-start",
                                                    mx: 0,
                                                    my: 0.5,
                                                    p: 1,
                                                    borderRadius: 1.5,
                                                    bgcolor: answers[question.id] === option.label ? "action.selected" : "transparent",
                                                }}
                                                label={
                                                    <Box sx={{ pt: 0.75 }}>
                                                        <Typography variant="body2" fontWeight={500}>{option.label}</Typography>
                                                        {option.description && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {option.description}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        ))}
                                    </RadioGroup>
                                    {errors[question.id] && (
                                        <Typography variant="caption" color="error">{errors[question.id]}</Typography>
                                    )}
                                </FormControl>
                            ) : (
                                <TextField
                                    key={question.id}
                                    label={question.question}
                                    value={answers[question.id] || ""}
                                    onChange={(event) => {
                                        setAnswers((current) => ({ ...current, [question.id]: event.target.value }));
                                        setErrors((current) => ({ ...current, [question.id]: "" }));
                                    }}
                                    error={Boolean(errors[question.id])}
                                    helperText={errors[question.id]}
                                    multiline
                                    minRows={2}
                                    required
                                    fullWidth
                                />
                            )
                        ))}

                        <Alert severity="info" sx={{ borderRadius: 2 }}>{PRIVACY_NOTICE}</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        pb: isMobile ? "max(16px, env(safe-area-inset-bottom))" : 2,
                        gap: 1,
                        flexDirection: isMobile ? "column-reverse" : "row",
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button onClick={closeRequest} disabled={submitting} fullWidth={isMobile}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={continueToConfirmation}
                        disabled={submitting}
                        fullWidth={isMobile}
                        sx={{ py: isMobile ? 1.25 : undefined }}
                    >
                        Review request
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={submitting ? undefined : () => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        borderRadius: isMobile ? 0 : 3,
                        m: isMobile ? 0 : 2,
                    },
                }}
            >
                <DialogTitle sx={{ pr: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                        <Typography variant="h6" component="span" fontWeight={700}>Confirm contract request</Typography>
                        <IconButton
                            onClick={() => setConfirmOpen(false)}
                            disabled={submitting}
                            edge="end"
                            aria-label="Close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
                    <Stack spacing={2} sx={{ mt: 0.5 }}>
                        <Typography>
                            Submit a request for <strong>{selectedOffering?.name}</strong>, starting {formatEuropeanDate(startDate)},
                            with payments due on day {paymentDay} of each month?
                        </Typography>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: 1,
                                borderColor: "divider",
                            }}
                        >
                            <Stack spacing={1.25} divider={<Divider flexItem />}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Start date</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatEuropeanDate(startDate)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Payment day</Typography>
                                    <Typography variant="body2" fontWeight={600}>Day {paymentDay}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                                    <Typography variant="body2" color="text.secondary">Monthly fee</Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCurrency(selectedOffering?.monthlyPrice, paymentCurrency)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>{PRIVACY_NOTICE}</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 2,
                        pb: isMobile ? "max(16px, env(safe-area-inset-bottom))" : 2,
                        gap: 1,
                        flexDirection: isMobile ? "column-reverse" : "row",
                        borderTop: 1,
                        borderColor: "divider",
                    }}
                >
                    <Button onClick={() => setConfirmOpen(false)} disabled={submitting} fullWidth={isMobile}>Back</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={submitting}
                        fullWidth={isMobile}
                        sx={{ py: isMobile ? 1.25 : undefined }}
                    >
                        {submitting ? "Submitting..." : "Confirm and submit"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={() => setNotification((current) => ({ ...current, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    severity={notification.severity}
                    onClose={() => setNotification((current) => ({ ...current, open: false }))}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ContractRequestPlans;
